import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Unlock, Save, DollarSign, Loader2 } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

/**
 * Onglet admin "Tarifs & Premium" : verrouille modules + définit prix CDF / USD.
 */
export default function AdminPricing({ token }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiCall('/admin/pricing-all', 'GET', null, token);
      setItems(r);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const update = (i, patch) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const save = async (i) => {
    const it = items[i];
    setSaving(prev => ({ ...prev, [i]: true }));
    try {
      await apiCall('/admin/pricing', 'PUT', {
        module_id: it.module_id,
        is_premium: !!it.is_premium,
        price_usd: it.price_usd ? Number(it.price_usd) : null,
        price_cdf: it.price_cdf ? Number(it.price_cdf) : null,
        description: it.description || '',
      }, token);
      toast.success('Tarif sauvegardé');
    } catch (e) { toast.error(e.message); }
    setSaving(prev => ({ ...prev, [i]: false }));
  };

  if (loading) return <div className="text-center py-10 text-white/60"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div data-testid="admin-pricing">
      <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><DollarSign size={18} className="text-yellow-400" />Tarifs & Premium</h3>
      <p className="text-sm text-white/60 mb-5">Active "Premium" sur les modules à monétiser. Définis les prix en USD et/ou en CDF.</p>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.module_id} className={`rounded-2xl p-4 border ${it.is_premium ? 'bg-yellow-500/5 border-yellow-400/30' : 'bg-white/5 border-white/10'}`} data-testid={`pricing-${it.module_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{it.icon}</span>
                <div>
                  <div className="font-bold">{it.title}</div>
                  <div className="text-xs text-white/50">{it.level}</div>
                </div>
              </div>
              <button onClick={() => update(i, { is_premium: !it.is_premium })} data-testid={`toggle-premium-${it.module_id}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition ${it.is_premium ? 'bg-yellow-400 text-slate-950' : 'bg-white/10 text-white/70 hover:bg-white/15'}`}>
                {it.is_premium ? <><Lock size={12} />Premium activé</> : <><Unlock size={12} />Gratuit</>}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-white/50 block mb-1">Prix USD</label>
                <input type="number" step="0.01" value={it.price_usd ?? ''} onChange={e => update(i, { price_usd: e.target.value })}
                  placeholder="Ex: 9.99" data-testid={`price-usd-${it.module_id}`}
                  className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-white/50 block mb-1">Prix CDF</label>
                <input type="number" step="100" value={it.price_cdf ?? ''} onChange={e => update(i, { price_cdf: e.target.value })}
                  placeholder="Ex: 25000" data-testid={`price-cdf-${it.module_id}`}
                  className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-white/50 block mb-1">Description courte</label>
                <input value={it.description ?? ''} onChange={e => update(i, { description: e.target.value })}
                  placeholder="Argument de vente" data-testid={`desc-${it.module_id}`}
                  className="w-full bg-slate-900 border border-white/10 rounded-lg p-2 text-sm" />
              </div>
            </div>
            <button onClick={() => save(i)} disabled={saving[i]} data-testid={`save-pricing-${it.module_id}`}
              className="mt-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold py-1.5 px-4 rounded-lg text-sm flex items-center gap-1.5">
              {saving[i] ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}Sauvegarder
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
